import { createSlice, createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
    cancelHunt,
    deleteHuntUser,
    getHuntDeclinedUsers,
    getHuntInvites,
    getHuntPendingUsers,
    getHunters,
    getHunts,
    huntAcceptInvite,
    huntDeclineInvite,
} from '../actions/huntActions';
import { User } from '../../types/user';

export const huntAdapter = createEntityAdapter();

export type Hunt = {
    id: number;
    name: string;
    ongoing: number;
    start: string;
    end: string;
    team_id: number;
    user_id: number;
    public: number;
    canceled: number;
    description: string;
    geofence_id: number;
};

export type HuntInvitation = Hunt;

const INITIAL_STATE = {
    hunts: [] as Hunt[],
    invites: [] as Hunt[],
    activeHunt: undefined,
    team: [] as User[],
    pendingUsers: [] as User[],
    declinedUsers: [] as User[],
};

const huntReducer = createSlice({
    name: 'hunt',
    initialState: huntAdapter.getInitialState(INITIAL_STATE),
    reducers: {
        setActiveHunt(state, action) {
            state.activeHunt = action.payload;
        },
        resetPendingAndDeclined(state) {
            state.pendingUsers = [];
            state.declinedUsers = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getHunts.fulfilled, (state, action) => {
                state.hunts = action.payload;
            })
            .addCase(getHunts.rejected, () => {})
            .addCase(cancelHunt.fulfilled, (state, action) => {
                state.hunts = state.hunts.filter((h) => h.id !== action.payload.id);
            })
            .addCase(cancelHunt.rejected, () => {})
            .addCase(deleteHuntUser.fulfilled, (state, action) => {
                state.team = action.payload;
            })
            .addCase(deleteHuntUser.rejected, () => {})
            .addCase(getHunters.fulfilled, (state, action) => {
                state.team = action.payload;
            })
            .addCase(getHunters.rejected, () => {})
            .addCase(getHuntPendingUsers.fulfilled, (state, action) => {
                state.pendingUsers = action.payload;
            })
            .addCase(getHuntPendingUsers.rejected, () => {})
            .addCase(getHuntDeclinedUsers.fulfilled, (state, action) => {
                state.declinedUsers = action.payload;
            })
            .addCase(getHuntDeclinedUsers.rejected, () => {})
            .addCase(huntDeclineInvite.fulfilled, (state, action) => {
                if (action.payload.id !== false) {
                    state.invites = state.invites.filter((i) => i.id !== action.payload.id);
                }
            })
            .addCase(huntDeclineInvite.rejected, () => {})
            .addCase(huntAcceptInvite.fulfilled, (state, action) => {
                if (action.payload.id !== false) {
                    state.invites = state.invites.filter((i) => i.id !== action.payload.id);
                }
            })
            .addCase(huntAcceptInvite.rejected, () => {})
            .addCase(getHuntInvites.fulfilled, (state, action) => {
                state.invites = action.payload;
            })
            .addCase(getHuntInvites.rejected, () => {});
    },
});

export const { setActiveHunt, resetPendingAndDeclined } = huntReducer.actions;

export default huntReducer.reducer;

const huntState$ = (s: RootState) => s.hunt;
const teamState$ = (s: RootState) => s.team;

export const sharedTeams$ = createSelector(teamState$, (s) => s.sharedTeams);

export const rawHunts$ = createSelector(huntState$, (s) => s.hunts);
export const huntTeamMembers$ = createSelector(huntState$, (s) => s.team);
export const activeHuntId$ = createSelector(huntState$, (s) => s.activeHunt);

export const currentHunt$ = createSelector(rawHunts$, activeHuntId$, (hunts, huntId) => {
    return hunts.find((h) => h.id === huntId);
});

export const huntTeam$ = createSelector(sharedTeams$, currentHunt$, (teams, hunt) => {
    return teams.find((h) => h.id === hunt?.team_id);
});

export const huntTeamLeader$ = createSelector(huntTeamMembers$, currentHunt$, (members, hunt) => {
    return members?.find((n) => n.user_id === hunt?.user_id);
});

export const huntInvitations$ = createSelector(huntState$, (s) => s.invites);
export const ongoingHunts$ = createSelector(rawHunts$, (hunts) =>
    hunts
        .filter((hunt) => hunt.ongoing === 1)
        .sort((a, b) => {
            return Date.parse(a.start.replace(/\s/g, 'T')) - Date.parse(b.start.replace(/\s/g, 'T'));
        }),
);
export const plannedHunts$ = createSelector(rawHunts$, (hunts) =>
    hunts
        .filter((hunt) => hunt.ongoing >= 0)
        .sort((a, b) => {
            return (
                b.ongoing - a.ongoing ||
                Date.parse(a.start.replace(/\s/g, 'T')) - Date.parse(b.start.replace(/\s/g, 'T'))
            );
        }),
);
export const upcomingHunts$ = createSelector(rawHunts$, (hunts) =>
    hunts
        .filter((hunt) => hunt.ongoing == 0)
        .sort((a, b) => {
            return (
                b.ongoing - a.ongoing ||
                Date.parse(a.start.replace(/\s/g, 'T')) - Date.parse(b.start.replace(/\s/g, 'T'))
            );
        }),
);
export const calendarHunts$ = createSelector(rawHunts$, (hunts) =>
    hunts.map((hunt) => {
        return {
            ...hunt,
            title: hunt.name,
            start: new Date(hunt.start),
            end: new Date(hunt.end),
            allDay: false,
        };
    }),
);

export const finishedHunts$ = createSelector(rawHunts$, (hunts) =>
    hunts
        .filter((hunt) => hunt.ongoing < 0)
        .sort((a, b) => {
            return Date.parse(a.start.replace(/\s/g, 'T')) - Date.parse(b.start.replace(/\s/g, 'T'));
        }),
);
